import { Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileType } from 'entities/Files/types';
import { FC, useMemo } from 'react';
import { ColorPalette } from 'shared/consts/colorPalette';
import { isEmptyObject } from 'shared/helpers/isEmptyObject';
import { CollapsableTable } from 'shared/ui/tables/CollapsableTable';
import { useExtension } from 'shared/hooks/useExtension';
import { useModelsOptions } from 'shared/hooks/use-models-options';
import {
	FilesDashboardColumns,
	FileForDashboard,
} from 'widgets/files/FilesDashboard/consts/DashboardColumns';
import { useAssertionSetsStore } from 'widgets/Extensions/Details/ui/AssertionSets/model/state';
import { ExtensionsEnum } from 'entities/Extensions/enums';
import { useAuthStore } from 'processes/auth/model/state';
import { ProcessFile } from 'widgets/files/FilesDashboard/consts/DashboardColumns';
import { useCustomModelStore } from 'widgets/customModel/state';
import { Prefixes } from 'app/config/routes/consts';
import { SearchParams } from 'shared/helpers/useFilesSearchParams';
import { SelectOption } from 'shared/ui/Select/types';
import { SelectedFilter } from './ui/SelectedFilter';

interface Props {
	data: {
		files: Record<string, Array<FileType>>;
		page: number;
		perPage: number;
		totalCount: number;
	};
	filesCount: number;
	selectedFiles: Array<string>;
	isAllSelected: boolean;
	isIndeterminate: boolean;
	handleToggleFile: (fileId: string) => void;
	handleToggleAllFiles: (refresh?: boolean) => void;
	handleEditFile: (fileId: string) => void;
	handleProcessFile: (process: ProcessFile, fileId?: string) => void;
	handleDeleteFile: (fileId?: string) => void;
	handleAssertions: (fileId?: string) => void;
	onPageChange: (page: number) => void;
	exportToErp: (fileId?: string) => void;
	exportToFaterErp: (fileId?: string) => void;
	searchParams: SearchParams;
	changeSearchParams: (params: Partial<SearchParams>) => void;
	uploaderOptions: Array<SelectOption>;
}

export const FilesDashboard: FC<Props> = ({
	data,
	filesCount,
	selectedFiles,
	isAllSelected,
	isIndeterminate,
	handleToggleFile,
	handleToggleAllFiles,
	handleEditFile,
	handleProcessFile,
	handleDeleteFile,
	handleAssertions,
	onPageChange,
	exportToErp,
	exportToFaterErp,
	searchParams,
	changeSearchParams,
	uploaderOptions,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { partnerId } = useParams();
	const { company } = useAuthStore();
	const { assertionsSets } = useAssertionSetsStore();
	const { customModels, generativeModels } = useCustomModelStore();
	const { getExtension } = useExtension();
	const { getModelName } = useModelsOptions();

	const handleRemoveFilter = (params: Partial<SearchParams>) => {
		handleToggleAllFiles(true);
		changeSearchParams(params);
	};

	const faterModelIds = company?.metadata?.faterModelIds;

	const preparedForDashboardFiles = useMemo(() => {
		const preparedData: Record<string, Array<FileForDashboard>> = {};
		data.files &&
			Object.keys(data.files)?.forEach((key) => {
				const subFiles = data.files[key];
				preparedData[`${key} \u00A0\u00A0\u00A0 ${subFiles.length} File(-s)`] = subFiles.map(
					(file) => ({
						...file,
						id: file._id,
						filesCount,
						selectedFiles,
						isAllSelected,
						isIndeterminate,
						assertionsIsEnabled: !!getExtension(ExtensionsEnum.assertions_sets)?.isEnabled,
						assertionSetsId:
							assertionsSets?.find((item) => item._id === file?.assertionSetsId)?.name || '-',
						toggleFile: handleToggleFile,
						toggleAllFiles: handleToggleAllFiles,
						edit: handleEditFile,
						processFile: handleProcessFile,
						deleteFile: handleDeleteFile,
						assertionsHandler: handleAssertions,
						exportToErp,
						exportToFaterErp,
						isPartnerSection: Boolean(partnerId),
						customModelName:
							customModels.find((item) => item._id === file.customModelId)?.name || '',
						generativeModelName:
							generativeModels.find((item) => item._id === file.generativeModelId)?.name || '',
						edilDeltaErpAutoExport: !!company?.metadata?.edilDeltaCustomModelId,
						faterErpAutoExport: Boolean(
							faterModelIds?.length &&
								((file.customModelId && faterModelIds.includes(file.customModelId)) ||
									(file.generativeModelId &&
										faterModelIds.includes(file.generativeModelId))),
						),
						onRowChange: () => navigate(`/${Prefixes.editFile}${file._id}`),
					}),
				);
			});

		return preparedData;
	}, [
		data.files,
		filesCount,
		selectedFiles,
		isAllSelected,
		isIndeterminate,
		handleToggleFile,
		handleToggleAllFiles,
		handleEditFile,
		handleProcessFile,
		handleDeleteFile,
		assertionsSets,
		customModels,
	]);

	return (
		<>
			{(searchParams.uploadSource !== 'all' ||
				!!searchParams.models.length ||
				!!searchParams.uploaderIdsFilter.length ||
				searchParams.importFrom ||
				searchParams.autoRemoveFrom) && (
				<Box sx={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
					{searchParams.uploadSource !== 'all' && (
						<SelectedFilter
							filterName={t('files.labels.import_option')}
							chips={[
								{
									label: searchParams.uploadSource,
									onDelete: () => handleRemoveFilter({ uploadSource: 'all' }),
								},
							]}
						/>
					)}
					{searchParams.importFrom && searchParams.importTo && (
						<SelectedFilter
							filterName={t('files.labels.import_date')}
							chips={[
								{
									label: `${searchParams.importFrom
										.slice(0, 10)
										.replaceAll('-', '/')} - ${searchParams.importTo
										.slice(0, 10)
										.replaceAll('-', '/')}`,
									onDelete: () =>
										handleRemoveFilter({ importFrom: undefined, importTo: undefined }),
								},
							]}
						/>
					)}
					{searchParams.autoRemoveFrom && searchParams.autoRemoveTo && (
						<SelectedFilter
							filterName={t('files.labels.removal_date')}
							chips={[
								{
									label: `${searchParams.autoRemoveFrom
										.slice(0, 10)
										.replaceAll('-', '/')} - ${searchParams.autoRemoveTo
										.slice(0, 10)
										.replaceAll('-', '/')}`,
									onDelete: () =>
										handleRemoveFilter({
											autoRemoveFrom: undefined,
											autoRemoveTo: undefined,
										}),
								},
							]}
						/>
					)}
					{!!searchParams.models.length && (
						<SelectedFilter
							filterName={t('files.labels.category')}
							chips={searchParams.models.map((model) => {
								const modelId =
									model.generativeModelId || model.customModelId || model.category;
								return {
									label: getModelName(modelId) || modelId,
									onDelete: () =>
										handleRemoveFilter({
											models: searchParams.models.filter(
												(m) =>
													(m.generativeModelId || m.customModelId || m.category) !==
													modelId,
											),
										}),
								};
							})}
						/>
					)}
					{!!searchParams.uploaderIdsFilter.length && (
						<SelectedFilter
							filterName={t('files.labels.import_user')}
							chips={searchParams.uploaderIdsFilter.map((id) => ({
								label: uploaderOptions.find((o) => o.value === id)?.label ?? id,
								onDelete: () =>
									handleRemoveFilter({
										uploaderIdsFilter: searchParams.uploaderIdsFilter.filter(
											(uid) => uid !== id,
										),
									}),
							}))}
						/>
					)}
				</Box>
			)}

			{data.files && !isEmptyObject(data.files) ? (
				<CollapsableTable
					columns={FilesDashboardColumns}
					subTables={preparedForDashboardFiles}
					pagination={{
						page: data?.page || 0,
						totalCount: data?.totalCount || 0,
						perPage: data?.perPage || 0,
						onPageChange,
					}}
				/>
			) : (
				<Typography
					variant={'subtitle1'}
					color={ColorPalette.astronaut}
					sx={{ margin: 'auto', width: '100%', textAlign: 'center' }}
				>
					{t('shared_texts.no_files')}
				</Typography>
			)}
		</>
	);
};
